/* LiberNovo landing page — shared behaviours.
   Public API: window.LibernovoLanding.{ init, setTab, prefersReducedMotion }
   Contract used by every libernovo-* section:
     .ln-anim                    entrance fade-up
     [data-ln-swiper='{json}']   Swiper 11 init, instance stored on el.lnSwiper
     [data-ln-tabs]              [data-ln-tab="key"] buttons + [data-ln-panel="key"] panels,
                                 optional data-ln-autorotate="ms", emits 'ln:tab' {key}
                                 state helper stored on el.lnTabs
     video[data-ln-video]        lazy source pick + optional data-ln-autoplay
     [data-ln-advance-on-ended]  video 'ended' advances the tabs inside it
*/
(function () {
  'use strict';

  var W = window;
  var D = document;

  /* Idempotency: WeakSets rather than a single attribute, because one element can
     legitimately play two roles (e.g. a .ln-anim wrapper that is also a tabs root). */
  var doneAnim = new WeakSet();
  var doneSwiper = new WeakSet();
  var doneTabs = new WeakSet();
  var doneVideo = new WeakSet();
  var doneAdvance = new WeakSet();

  var lazyVideos = new Set();

  function prefersReducedMotion() {
    return !!(W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isDesktop() {
    return !W.matchMedia || W.matchMedia('(min-width: 768px)').matches;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* Only elements that belong to THIS root, not to a nested container of the same kind. */
  function scoped(root, selector, boundary) {
    return toArray(root.querySelectorAll(selector)).filter(function (el) {
      return el.closest(boundary) === root;
    });
  }

  function stamp(el) {
    el.setAttribute('data-ln-ready', 'true');
  }

  /* ------------------------------------------------------------------ *
   * 1. Entrance animation
   * ------------------------------------------------------------------ */

  var animObserver = null;

  function animObserve() {
    var els = toArray(D.querySelectorAll('.ln-anim')).filter(function (el) {
      return !doneAnim.has(el);
    });
    if (!els.length) return;

    if (!('IntersectionObserver' in W)) {
      els.forEach(function (el) {
        doneAnim.add(el);
        el.classList.add('is-in');
      });
      return;
    }

    if (!animObserver) {
      animObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            animObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -12% 0px' }
      );
    }

    els.forEach(function (el) {
      doneAnim.add(el);
      animObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Swipers
   * ------------------------------------------------------------------ */

  var swiperRetries = 0;

  function initSwipers() {
    var els = toArray(D.querySelectorAll('[data-ln-swiper]')).filter(function (el) {
      return !doneSwiper.has(el);
    });
    if (!els.length) return;

    if (typeof W.Swiper !== 'function') {
      /* swiper-bundle.min.js is a deferred sibling; retry until it lands. */
      if (swiperRetries < 20) {
        swiperRetries += 1;
        W.setTimeout(initSwipers, 150);
        W.addEventListener('load', initSwipers, { once: true });
      }
      return;
    }

    els.forEach(function (el) {
      var config = { speed: 600 };
      var raw = el.getAttribute('data-ln-swiper');
      if (raw) {
        try {
          var parsed = JSON.parse(raw);
          Object.keys(parsed).forEach(function (key) {
            config[key] = parsed[key];
          });
        } catch (err) {
          /* malformed JSON must not take the whole page down */
        }
      }
      if (prefersReducedMotion()) delete config.autoplay;

      /* Trackpads: a two-finger sideways swipe must move the slider, not fall
         through to the browser as "back". Card rows also glide freely and settle
         on the nearest card (like the homepage product rows); heroes (one slide
         at a time) step one slide per swipe. Vertical scrolling is untouched. */
      if (!config.mousewheel) config.mousewheel = { forceToAxis: true };
      var isHero = el.classList.contains('lnhero__swiper') || !!el.closest('.lnhero, [data-bl-hero]');
      if (!isHero && !config.loop && !config.autoplay && !config.centeredSlides && !config.freeMode) {
        config.freeMode = { enabled: true, sticky: true, momentumBounce: false };
        config.grabCursor = true;
      }

      doneSwiper.add(el);
      stamp(el);
      try {
        el.lnSwiper = new W.Swiper(el, config);
      } catch (err) {
        /* leave the markup as a plain scroller */
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Tabs
   * ------------------------------------------------------------------ */

  function setTab(root, key, opts) {
    if (!root || !root.lnTabs) return;
    root.lnTabs.set(key, opts || {});
  }

  function initTabs(root) {
    if (doneTabs.has(root)) return;
    doneTabs.add(root);
    stamp(root);

    var buttons = scoped(root, '[data-ln-tab]', '[data-ln-tabs]');
    var panels = scoped(root, '[data-ln-panel]', '[data-ln-tabs]');
    if (!buttons.length) return;

    var keys = buttons.map(function (btn) {
      return btn.getAttribute('data-ln-tab');
    });
    var current = null;
    var timer = null;
    var paused = false;
    var stopped = false;
    var interval = parseInt(root.getAttribute('data-ln-autorotate'), 10) || 0;

    function apply(key, opts) {
      if (keys.indexOf(key) === -1) return;
      var changed = key !== current;
      current = key;

      buttons.forEach(function (btn) {
        var on = btn.getAttribute('data-ln-tab') === key;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
        if (btn.hasAttribute('role')) btn.tabIndex = on ? 0 : -1;
      });

      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.getAttribute('data-ln-panel') === key);
      });

      root.dispatchEvent(
        new CustomEvent('ln:tab', {
          bubbles: true,
          detail: {
            key: key,
            index: keys.indexOf(key),
            changed: changed,
            source: (opts && opts.source) || 'api'
          }
        })
      );
    }

    function next() {
      var i = keys.indexOf(current);
      apply(keys[(i + 1) % keys.length], { source: 'auto' });
    }

    function schedule() {
      clear();
      if (!interval || stopped || paused || prefersReducedMotion()) return;
      timer = W.setInterval(next, interval);
    }

    function clear() {
      if (timer) {
        W.clearInterval(timer);
        timer = null;
      }
    }

    function pause() {
      paused = true;
      clear();
    }

    function resume() {
      if (stopped) return;
      paused = false;
      schedule();
    }

    function stop() {
      stopped = true;
      clear();
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        if (btn.tagName === 'A') event.preventDefault();
        stop(); /* a deliberate choice outranks the carousel from here on */
        apply(btn.getAttribute('data-ln-tab'), { source: 'click' });
      });
    });

    root.lnTabs = {
      keys: keys,
      set: apply,
      next: next,
      stop: stop,
      pause: pause,
      resume: resume,
      current: function () {
        return current;
      }
    };

    /* initial state: whatever the markup marked active, else the first tab */
    var preset = buttons.filter(function (btn) {
      return btn.classList.contains('is-active');
    })[0];
    apply(preset ? preset.getAttribute('data-ln-tab') : keys[0], { source: 'init' });

    if (interval) {
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
      root.addEventListener('touchstart', pause, { passive: true });

      if ('IntersectionObserver' in W) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) resume();
              else pause();
            });
          },
          { threshold: 0.2 }
        ).observe(root);
      } else {
        schedule();
      }
    }
  }

  function initAllTabs() {
    toArray(D.querySelectorAll('[data-ln-tabs]')).forEach(initTabs);
  }

  /* ------------------------------------------------------------------ *
   * 4. Lazy videos
   * ------------------------------------------------------------------ */

  var videoLoadObserver = null;
  var videoPlayObserver = null;

  function pickSrc(video) {
    var desktop = video.getAttribute('data-src-desktop');
    var mobile = video.getAttribute('data-src-mobile');
    var chosen = isDesktop() ? desktop || mobile : mobile || desktop;
    return chosen || video.getAttribute('data-src') || '';
  }

  function loadVideo(video) {
    if (video.getAttribute('data-ln-loaded') === 'true') return;

    var src = pickSrc(video);
    if (src) {
      video.setAttribute('data-ln-loaded', 'true');
      video.src = src;
      video.load();
      return;
    }

    var sources = toArray(video.querySelectorAll('source[data-src]'));
    var any = false;
    sources.forEach(function (source) {
      var value = source.getAttribute('data-src');
      if (!value) return;
      source.src = value;
      any = true;
    });
    if (!any) return;
    video.setAttribute('data-ln-loaded', 'true');
    video.load();
  }

  function playVideo(video) {
    if (prefersReducedMotion()) return;
    if (!video.hasAttribute('data-ln-autoplay')) return;
    loadVideo(video);
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* autoplay refused (low power mode etc.) — the poster stays up */
      });
    }
  }

  function pauseVideo(video) {
    if (!video.paused) video.pause();
  }

  function initVideos() {
    var vids = toArray(D.querySelectorAll('video[data-ln-video]')).filter(function (v) {
      return !doneVideo.has(v);
    });
    if (!vids.length) return;

    if (!('IntersectionObserver' in W)) {
      vids.forEach(function (v) {
        doneVideo.add(v);
        lazyVideos.add(v);
        loadVideo(v);
      });
      return;
    }

    if (!videoLoadObserver) {
      videoLoadObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            loadVideo(entry.target);
            videoLoadObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '200px' }
      );
    }

    if (!videoPlayObserver) {
      videoPlayObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) playVideo(entry.target);
            else pauseVideo(entry.target);
          });
        },
        { threshold: 0.25 }
      );
    }

    vids.forEach(function (v) {
      doneVideo.add(v);
      lazyVideos.add(v);
      stamp(v);
      videoLoadObserver.observe(v);
      if (v.hasAttribute('data-ln-autoplay')) videoPlayObserver.observe(v);
    });
  }

  D.addEventListener('visibilitychange', function () {
    if (!D.hidden) return;
    lazyVideos.forEach(function (v) {
      if (!v.isConnected) {
        lazyVideos.delete(v);
        return;
      }
      pauseVideo(v);
    });
  });

  /* ------------------------------------------------------------------ *
   * 5. Video-driven tab advance
   * ------------------------------------------------------------------ */

  function initAdvanceOnEnded() {
    toArray(D.querySelectorAll('[data-ln-advance-on-ended]')).forEach(function (root) {
      if (doneAdvance.has(root)) return;
      doneAdvance.add(root);

      /* 'ended' does not bubble — listen in the capture phase. */
      root.addEventListener(
        'ended',
        function (event) {
          var video = event.target;
          if (!video || video.tagName !== 'VIDEO') return;

          var tabsRoot = root.matches('[data-ln-tabs]') ? root : root.querySelector('[data-ln-tabs]');
          if (!tabsRoot || !tabsRoot.lnTabs) return;

          var panel = video.closest('[data-ln-panel]');
          if (panel && panel.getAttribute('data-ln-panel') !== tabsRoot.lnTabs.current()) return;

          tabsRoot.lnTabs.next();
        },
        true
      );
    });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function init() {
    animObserve();
    initSwipers();
    initAllTabs();
    initVideos();
    initAdvanceOnEnded();
  }

  W.LibernovoLanding = {
    init: init,
    setTab: setTab,
    loadVideo: loadVideo,
    prefersReducedMotion: prefersReducedMotion
  };

  if (D.readyState === 'loading') {
    D.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  D.addEventListener('shopify:section:load', init);
})();


/* Overflow self-audit: append ?ln_debug=1 to the URL to list elements wider
   than the viewport in an overlay. Inert without the parameter. */
(function () {
  if (!/[?&]ln_debug=1/.test(location.search)) return;
  var report = function () {
    var vw = window.innerWidth;
    var rows = ['vw=' + vw + ' scrollW=' + document.documentElement.scrollWidth + ' bodyW=' + document.body.scrollWidth];
    var clipped = function (el) {
      var n = el.parentElement;
      while (n && n !== document.body) {
        if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(n).overflowX)) return true;
        n = n.parentElement;
      }
      return false;
    };
    rows.push('htmlOX=' + getComputedStyle(document.documentElement).overflowX + ' bodyOX=' + getComputedStyle(document.body).overflowX);
    var all = document.body.querySelectorAll('*');
    for (var i = 0; i < all.length && rows.length < 15; i++) {
      var el = all[i];
      if (el.id === 'ln-debug-overlay') continue;
      var r = el.getBoundingClientRect();
      var pos = getComputedStyle(el).position;
      var escapesClip = pos === 'absolute' || pos === 'fixed';
      if (r.right > vw + 3 && r.width > 40 && (escapesClip || !clipped(el))) {
        rows.push(el.tagName + '.' + String(el.className).split(' ').slice(0, 2).join('.') + '[' + pos + '] L' + Math.round(r.left) + ' R' + Math.round(r.right));
      }
    }
    var box = document.getElementById('ln-debug-overlay');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'ln-debug-overlay';
      box.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;background:#000;color:#0f0;font:10px/1.5 monospace;padding:6px;margin:0;white-space:pre-wrap;max-height:45vh;overflow:auto;';
      document.body.appendChild(box);
    }
    box.textContent = rows.join('\n');
  };
  window.addEventListener('load', function () { setTimeout(report, 1200); });
  if (document.readyState === 'complete') setTimeout(report, 1200);
})();
